import React, { useEffect } from 'react';
import SEO from '../SEO';
import seoData from '../../seoData';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { CheckCircle, ArrowRight, Shield, Droplets, Waves, Wrench } from 'lucide-react';
import Header from '../Header';
import Footer from '../Footer';
import PageHero from '../ui/PageHero';
import { useNavigate } from 'react-router-dom';

const WaterWastewater = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <SEO title={seoData["industries/water-wastewater"]?.title} description={seoData["industries/water-wastewater"]?.description} path="/industries/water-wastewater" keywords={seoData["industries/water-wastewater"]?.keywords || []} />

      <PageHero
        badge="Industries We Serve"
        title="Water & Wastewater"
        description="Certified wastewater coating contractor and water tank lining specialist serving California municipalities and water districts. Clarifier coating repair, wet well coating, potable water tank lining, secondary containment, and treatment plant coatings. NSF 61 certified systems."
        backgroundKey="water-wastewater"
        buttonText="Get Industry Consultation"
      />

      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Industry Challenges We Solve</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {['Clarifier and basin corrosion from hydrogen sulfide (H2S) and chemical attack',
              'Potable water tank interior deterioration and coating failure',
              'Wet well and lift station corrosion from sewage gases',
              'Concrete spalling and rebar exposure in aeration basins and channels',
              'Secondary containment failures at chemical feed areas',
              'Pipe and valve deterioration from aggressive water chemistry'].map((challenge, index) => (
              <div key={index} className="flex items-start p-4 bg-cyan-50 rounded-lg">
                <Droplets className="text-cyan-600 mr-3 mt-0.5 flex-shrink-0" size={20} />
                <span className="text-gray-700 text-sm">{challenge}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Our Solutions</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <Card className="border-l-4 border-l-cyan-600">
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <Waves className="text-cyan-600 mr-3 flex-shrink-0" size={22} />
                  Clarifier Coating & Repair
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm leading-relaxed">Complete clarifier rehabilitation including concrete repair, rebar protection, and application of chemical-resistant epoxy coating systems. BCS is a certified clarifier coating contractor with proven solutions for circular and rectangular clarifiers, settling basins, and flocculation chambers.</p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-cyan-600">
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <Waves className="text-cyan-600 mr-3 flex-shrink-0" size={22} />
                  Potable Water Tank Lining
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm leading-relaxed">NSF/ANSI 61 certified tank lining systems for potable water storage. Steel and concrete water tank coating for ground storage, elevated tanks, and reservoirs. Our water tank lining contractor services include surface preparation, interior coating, and full QA/QC documentation.</p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-cyan-600">
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <Waves className="text-cyan-600 mr-3 flex-shrink-0" size={22} />
                  Wet Well & Lift Station Coating
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm leading-relaxed">High-build, H2S-resistant coating systems for wet wells, lift stations, and sewage pump stations. Chemical-resistant linings that withstand the harshest wastewater environments and prevent concrete degradation from biogenic sulfuric acid attack.</p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-cyan-600">
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <Waves className="text-cyan-600 mr-3 flex-shrink-0" size={22} />
                  Secondary Containment & Chemical Feed
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm leading-relaxed">Containment coating systems for chemical storage and feed areas. Bund lining, chemical containment coatings, and spill prevention systems for sodium hypochlorite, ferric chloride, polymer, and other treatment chemicals.</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-slate-900 mb-6">Why Choose BCS for Water & Wastewater?</h2>
              <div className="space-y-3">
                {['NSF/ANSI 61 certified potable water coating systems for safe drinking water contact',
                  'Municipal and water district contractor — public works experience across California',
                  'Minimize service disruptions with phased construction and bypass pumping coordination',
                  'Extend infrastructure life 15-25 years vs. costly full replacement',
                  'Meet OSHA confined space requirements with trained and certified crews',
                  '24/7 emergency response for critical infrastructure failures and active leaks'].map((benefit, index) => (
                  <div key={index} className="flex items-start">
                    <CheckCircle className="text-green-700 mr-3 mt-0.5 flex-shrink-0" size={18} />
                    <span className="text-gray-700 text-sm">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-cyan-600 text-white p-8 rounded-lg">
              <Shield className="mb-4" size={48} />
              <h3 className="text-2xl font-bold mb-4">Municipal Water District Contractor</h3>
              <p className="text-cyan-100 mb-6">BCS is a certified public works and municipal coating contractor. We work with water districts, cities, and sanitation agencies across California on potable water, wastewater, and recycled water infrastructure.</p>
              <Button className="bg-white text-blue-900 hover:bg-gray-100" onClick={() => navigate('/services/tank-repair')}>
                View Tank Lining Services <ArrowRight className="ml-2" size={16} />
              </Button>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-blue-900 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-4">Protect Your Water & Wastewater Infrastructure</h2>
          <p className="text-blue-100 mb-8">Contact our water and wastewater coating specialists for a free facility assessment. We serve municipalities, water districts, and sanitation agencies across California.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" className="bg-white text-blue-900 hover:bg-gray-100" onClick={() => window.location.href = 'tel:+19493719948'}>
              Call (949) 371-9948
            </Button>
            <Button size="lg" variant="outline" className="border-white text-white hover:bg-blue-800" onClick={() => navigate('/#contact')}>
              Request Consultation <ArrowRight className="ml-2" size={20} />
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default WaterWastewater;
